import type { ContentHref } from '@generalTypes/apiTypes';
import { createTypedSelector, parentChildRelationFilter } from '@newStore/genericHelpers';
import { selectKrcReferenceFrameRelationsMap } from '@newStore/referenceFrames/referenceFramesSelectors';
import { escape as escapeHtml } from 'lodash';
import {
  type EditAsideKrcGoalRelations,
  type EditComponent,
  RequiredType,
} from '@nodeTypeConfig/configTypes';
import {
  selectAllKrcGoalRelations,
  selectKrcGoalAncestorContextMap,
  selectKrcGoalIdentifierMap,
  selectKrcGoalRelations,
  selectKrcPrecedesSuccessorsMap,
} from '@UI/aside/asideKrcGoalRelations/asideKrcGoalRelationsSelectors';
import { createError } from '../createError';
import type { AsideValidationRule } from '../validationTypes';

export const selectHasUniqueKrcGoalPairs: AsideValidationRule = createTypedSelector(
  [
    (state, href: ContentHref) => selectAllKrcGoalRelations(state, href),
    (state, href: ContentHref, parentHref, config: EditComponent) =>
      selectKrcGoalRelations(state, href, config as EditAsideKrcGoalRelations),
    (state, href: ContentHref, parentHref, config: EditComponent) => config,
  ],
  (allRelations, relationsForCurrentPanel, config) => {
    const relationCountPerTarget = new Map<ContentHref, number>();

    allRelations.forEach((relation) => {
      relationCountPerTarget.set(
        relation.targetHref,
        (relationCountPerTarget.get(relation.targetHref) || 0) + 1,
      );
    });

    const duplicatedRelations = relationsForCurrentPanel.filter((relation) => {
      return (relationCountPerTarget.get(relation.targetHref) || 0) > 1;
    });

    if (!duplicatedRelations.length) {
      return true;
    }

    const duplicatedGoals = duplicatedRelations
      .map((relation) => `${escapeHtml(relation.goalIdentifier)} - ${escapeHtml(relation.title)}`)
      .join(', ');

    return createError(
      'krcGoalDuplicateGoalPair',
      'selectHasUniqueKrcGoalPairs',
      `Hetzelfde <strong>doel</strong> mag niet in meerdere coherenties voorkomen. Dubbele coherentie met: ${duplicatedGoals}.`,
      {
        component: config.component,
      },
      RequiredType.WARNING,
    );
  },
);

export const selectKrcTogetherRelationsSameAgeRange: AsideValidationRule = createTypedSelector(
  [
    (state) => selectKrcGoalAncestorContextMap(state),
    (state, href: ContentHref, parentHref, config: EditComponent) =>
      selectKrcGoalRelations(state, href, config as EditAsideKrcGoalRelations),
    (state, href: ContentHref) => href,
    (state, href: ContentHref, parentHref, config: EditComponent) => config,
  ],
  (ancestorContextMap, relationsForCurrentPanel, currentGoalHref, config) => {
    const currentContext = ancestorContextMap.get(currentGoalHref);
    if (!currentContext?.goalSetThemeHref) return true;

    for (const relation of relationsForCurrentPanel) {
      const relatedContext = ancestorContextMap.get(relation.targetHref);
      if (!relatedContext) continue;

      if (currentContext.goalSetThemeHref !== relatedContext.goalSetThemeHref) continue;

      if (currentContext.ageRangeThemeHref !== relatedContext.ageRangeThemeHref) {
        return createError(
          'krcTogetherRelationsDifferentAgeRange',
          'selectKrcTogetherRelationsSameAgeRange',
          `Doelen die <strong>samen</strong> worden behandeld moeten in hetzelfde leeftijdsbereik. Doel met ander leeftijdsbereik: <strong>${escapeHtml(relation.goalIdentifier)}</strong> - ${escapeHtml(relation.title)}`,
          { component: config.component },
          RequiredType.WARNING,
        );
      }
    }

    return true;
  },
);

/**
 * For every KRC_AGE_RANGE node in the KRC reference frame, returns its position (0-based index)
 * among its sibling KRC_AGE_RANGE children under the same KRC_GOAL_SET, sorted by readorder.
 * Used to compare ordering of two age-range themes that belong to the same goal set.
 */
export const selectReferenceFrameAgeRangesPositionMap = createTypedSelector(
  [(state) => selectKrcReferenceFrameRelationsMap(state)],
  (relationsMap): Map<ContentHref, number> => {
    const positionMap = new Map<ContentHref, number>();
    if (!relationsMap) return positionMap;

    for (const parentHref of Object.keys(relationsMap.to) as ContentHref[]) {
      const childRelations = relationsMap.to[parentHref];
      if (!childRelations) continue;

      childRelations
        .filter(parentChildRelationFilter)
        .sort((a, b) => (a.readorder || 0) - (b.readorder || 0))
        .forEach((childRel, index) => {
          positionMap.set(childRel.from.href as ContentHref, index);
        });
    }

    return positionMap;
  },
);

export const selectKrcPrecedesRelationsAgeRangeValidation: AsideValidationRule =
  createTypedSelector(
    [
      (state) => selectKrcGoalAncestorContextMap(state),
      (state) => selectReferenceFrameAgeRangesPositionMap(state),
      (state, href: ContentHref, parentHref, config: EditComponent) =>
        selectKrcGoalRelations(state, href, config as EditAsideKrcGoalRelations),
      (state, href: ContentHref) => href,
      (state, href: ContentHref, parentHref, config: EditComponent) => config,
    ],
    (
      ancestorContextMap,
      ageRangePositionMap,
      relationsForCurrentPanel,
      currentGoalHref,
      config,
    ) => {
      const relationType = (config as EditAsideKrcGoalRelations).options.relationType;
      if (relationType !== 'PRECEDES_BEFORE' && relationType !== 'PRECEDES_AFTER') {
        return true;
      }

      const currentContext = ancestorContextMap.get(currentGoalHref);
      if (!currentContext?.goalSetThemeHref || !currentContext.ageRangeThemeHref) return true;

      const currentPosition = ageRangePositionMap.get(currentContext.ageRangeThemeHref);
      if (currentPosition === undefined) return true;

      for (const relation of relationsForCurrentPanel) {
        const relatedContext = ancestorContextMap.get(relation.targetHref);
        if (!relatedContext?.ageRangeThemeHref) continue;

        // Only meaningful to compare positions when both goals share the same goal set theme,
        // because the KRC_AGE_RANGE position map indexes ranges per parent KRC_GOAL_SET.
        if (currentContext.goalSetThemeHref !== relatedContext.goalSetThemeHref) continue;

        const relatedPosition = ageRangePositionMap.get(relatedContext.ageRangeThemeHref);
        if (relatedPosition === undefined) continue;

        const isInvalid =
          relationType === 'PRECEDES_BEFORE'
            ? relatedPosition > currentPosition
            : relatedPosition < currentPosition;

        if (isInvalid) {
          const directionLabel =
            relationType === 'PRECEDES_BEFORE' ? 'voor dit doel komt' : 'na dit doel komt';
          const expectationLabel =
            relationType === 'PRECEDES_BEFORE' ? 'eerder of gelijk' : 'later of gelijk';
          return createError(
            'krcPrecedesRelationsAgeRangeInvalid',
            'selectKrcPrecedesRelationsAgeRangeValidation',
            `Een doel dat <strong>${directionLabel}</strong> moet in een leeftijdsbereik zitten dat ${expectationLabel} is. Doel met verkeerd leeftijdsbereik: <strong>${escapeHtml(relation.goalIdentifier)}</strong> - ${escapeHtml(relation.title)}`,
            { component: config.component },
            RequiredType.WARNING,
          );
        }
      }

      return true;
    },
  );

/**
 * Flags PRECEDES relations in the current panel that are transitively redundant via another
 * PRECEDES chain that also passes through panel members.
 *
 * - AFTER panel (A -> {X, Y, ...}): if there is a forward PRECEDES path X ->+ Y where both X and
 *   Y are in the panel, then A -> Y is redundant (A -> X -> ... -> Y already implies it). Y is
 *   flagged.
 * - BEFORE panel ({X, Y, ...} -> A): if there is a forward PRECEDES path X ->+ Y where both X
 *   and Y are in the panel, then X -> A is redundant (X -> ... -> Y -> A already implies it).
 *   X is flagged.
 *
 * Uses `selectKrcPrecedesSuccessorsMap` (built once per state) and performs a bounded BFS per
 * panel member, short-circuiting as soon as enough panel members are reached.
 */
export const selectKrcPrecedesRelationsNoRedundancy: AsideValidationRule = createTypedSelector(
  [
    (state) => selectKrcPrecedesSuccessorsMap(state),
    (state) => selectKrcGoalIdentifierMap(state),
    (state, href: ContentHref, parentHref, config: EditComponent) =>
      selectKrcGoalRelations(state, href, config as EditAsideKrcGoalRelations),
    (state, href: ContentHref) => href,
    (state, href: ContentHref, parentHref, config: EditComponent) => config,
  ],
  (successorsMap, identifierMap, relationsForCurrentPanel, currentGoalHref, config) => {
    const relationType = (config as EditAsideKrcGoalRelations).options.relationType;
    if (relationType !== 'PRECEDES_BEFORE' && relationType !== 'PRECEDES_AFTER') return true;
    if (relationsForCurrentPanel.length < 2) return true;

    const memberSet = new Set<ContentHref>();
    const memberByHref = new Map<ContentHref, (typeof relationsForCurrentPanel)[number]>();
    for (const relation of relationsForCurrentPanel) {
      memberSet.add(relation.targetHref);
      memberByHref.set(relation.targetHref, relation);
    }

    // Each entry: a redundant edge in the current panel, with the full PRECEDES chain through
    // panel members that already implies it.
    //   AFTER:  redundant `A -> redundantHref` because chain `start -> ... -> redundantHref`.
    //   BEFORE: redundant `redundantHref -> A` because chain `redundantHref -> ... -> reached`.
    // `chain` is always written in PRECEDES direction (left precedes right).
    const redundancies: Array<{ redundantHref: ContentHref; chain: ContentHref[] }> = [];
    const seenRedundant = new Set<ContentHref>();

    for (const relation of relationsForCurrentPanel) {
      const start = relation.targetHref;
      const otherMembersToFind = memberSet.size - 1;
      if (otherMembersToFind === 0) break;

      // BFS forward via PRECEDES from `start`. BFS (vs DFS) guarantees the first time a panel
      // member is reached we have a shortest proof chain. We never enter the current goal
      // itself, which guards against cycles that would otherwise wrap back through A and
      // produce false positives. `visited` also prevents cycles elsewhere in the graph.
      const visited = new Set<ContentHref>([start, currentGoalHref]);
      const parentOf = new Map<ContentHref, ContentHref>();
      const queue: ContentHref[] = [start];
      let head = 0;
      const reachedMembers: ContentHref[] = [];

      while (head < queue.length) {
        const node = queue[head++];
        const successors = successorsMap.get(node);
        if (!successors) continue;

        for (const succ of successors) {
          if (visited.has(succ)) continue;
          visited.add(succ);
          parentOf.set(succ, node);
          if (memberSet.has(succ)) {
            reachedMembers.push(succ);
            if (reachedMembers.length === otherMembersToFind) {
              // All other panel members already proven reachable; stop expanding.
              head = queue.length;
              break;
            }
          }
          queue.push(succ);
        }
      }

      if (!reachedMembers.length) continue;

      const buildChain = (endHref: ContentHref): ContentHref[] => {
        const chain: ContentHref[] = [endHref];
        let cursor: ContentHref | undefined = endHref;
        while (cursor !== start) {
          const parent = parentOf.get(cursor);
          if (!parent) break;
          chain.unshift(parent);
          cursor = parent;
        }
        return chain;
      };

      if (relationType === 'PRECEDES_AFTER') {
        // A -> start -> ... -> reached  =>  A -> reached is redundant.
        for (const reached of reachedMembers) {
          if (seenRedundant.has(reached)) continue;
          seenRedundant.add(reached);
          redundancies.push({ redundantHref: reached, chain: buildChain(reached) });
        }
      } else {
        // BEFORE: start -> ... -> reached -> A  =>  start -> A is redundant. Pick the first
        // (shortest) reached member as proof.
        if (seenRedundant.has(start)) continue;
        seenRedundant.add(start);
        redundancies.push({ redundantHref: start, chain: buildChain(reachedMembers[0]) });
      }
    }

    if (!redundancies.length) return true;

    const formatHref = (href: ContentHref): string => {
      const label = memberByHref.get(href)?.goalIdentifier || identifierMap.get(href) || '?';
      return `<strong>${escapeHtml(label)}</strong>`;
    };

    const lines = redundancies.map(({ redundantHref, chain }) => {
      const chainLabel = chain.map(formatHref).join(' → ');
      return `${formatHref(redundantHref)} (al geïmpliceerd via ${chainLabel})`;
    });

    const directionLabel = relationType === 'PRECEDES_BEFORE' ? 'voor' : 'na';

    return createError(
      'krcPrecedesRelationsRedundant',
      'selectKrcPrecedesRelationsNoRedundancy',
      `Volgende <strong>${directionLabel}</strong>-relatie(s) zijn ongeldig omdat ze al onrechtstreeks via een ander doel bestaan: ${lines.join('; ')}`,
      { component: config.component },
      RequiredType.ERROR,
    );
  },
);

export const krcGoalRelationsValidationRules = [selectHasUniqueKrcGoalPairs];

export const krcPrecedesValidationRules = [
  selectHasUniqueKrcGoalPairs,
  selectKrcPrecedesRelationsAgeRangeValidation,
  selectKrcPrecedesRelationsNoRedundancy,
];

export const krcTogetherValidationRules = [
  selectHasUniqueKrcGoalPairs,
  selectKrcTogetherRelationsSameAgeRange,
];
